/**
 * The renderer: draws the project at time t. Used unchanged by the editor preview (t from the
 * audio clock) and the render worker (t = frame / fps).
 */

import { makeCanvas, type Ctx2D } from "./canvas";
import { drawBackground } from "./background";
import { drawCaption } from "./captions";
import { autoCameraKeys, cameraMatrix, makeCamera, resolveKeys, type CameraFn, type CameraState } from "./camera";
import { buildRuntime, drawElement, elementAlpha, elementKey, elementMatrix, eraserTip, penTip, type ElementRuntime } from "./element";
import { ERASER, imageHand, MARKER, type HandSprite } from "./hand";
import { apply, chain, clamp, ease, mul, rotate, scale, smoothstep, translate, type Mat } from "./math";
import type { CameraKey, Element, Project, Rect, Subtitle } from "./project.gen";
import type { Resources } from "./resources";
import { MaskRevealer } from "./reveal";
import { buildStrokeSet, PenTimeline, zigzagStroke } from "./strokes";
import { elementTimes, phaseAt, sceneIndexAt, sortedScenes, type ElementTimes } from "./timeline";

export interface RenderTarget {
  ctx: Ctx2D | CanvasRenderingContext2D;
  width: number;
  height: number;
}

/** Hand behaviour between pen phases. */
const HAND = {
  /** gaps shorter than this: the hand travels straight to the next job */
  bridgeGap: 0.9,
  /** time to leave / enter the frame */
  exit: 0.45,
  enter: 0.45,
  /** smoothing half-windows (s): wide while hatching fill so the hand doesn't jitter on zig-zags */
  fillSmooth: 0.03,
  drawSmooth: 0.004,
  liftSmooth: 0.02,
};

interface HandPoint {
  x: number;
  y: number;
  down: boolean;
  smooth: number;
}

/** A span of time during which the hand (or eraser) is doing something at a known place. */
interface HandInterval {
  t0: number;
  t1: number;
  sprite: HandSprite;
  /** output-pixel position at t, clamped into [t0, t1) */
  point(t: number, M: Mat): HandPoint | null;
}

export class Renderer {
  private project!: Project;
  private times = new Map<string, ElementTimes>();
  private runtimes = new Map<string, ElementRuntime>();
  private order: Element[] = [];
  private hands: HandInterval[] = [];
  private autoKeys: CameraKey[] = [];
  private keys: CameraKey[] = [];
  private cam: CameraFn = () => ({ x: 960, y: 540, zoom: 1, rotation: 0 });
  private clearLayer: { canvas: OffscreenCanvas; ctx: Ctx2D } | null = null;
  private erasers = new Map<string, { pen: PenTimeline; mask: MaskRevealer }>();
  private cues: Subtitle[] = [];

  constructor(
    project: Project,
    private resources: Resources,
    private target: RenderTarget,
  ) {
    this.setProject(project);
  }

  /** Swap in an edited project; runtimes are rebuilt only for elements that changed. */
  setProject(project: Project, resources?: Resources): void {
    if (resources) {
      this.resources = resources;
      this.runtimes.clear();
    }
    this.project = project;
    this.times = elementTimes(project);
    const live = new Set<string>();
    for (const el of project.elements) {
      live.add(el.id);
      const cur = this.runtimes.get(el.id);
      if (!cur || cur.key !== elementKey(el)) {
        const rt = buildRuntime(el, this.resources);
        if (rt) this.runtimes.set(el.id, rt);
        else this.runtimes.delete(el.id);
      }
    }
    for (const id of [...this.runtimes.keys()]) if (!live.has(id)) this.runtimes.delete(id);
    const idx = new Map(project.elements.map((e, i) => [e.id, i]));
    this.order = [...project.elements].sort(
      (a, b) => a.z - b.z || this.times.get(a.id)!.enter0 - this.times.get(b.id)!.enter0 || idx.get(a.id)! - idx.get(b.id)!,
    );
    this.updateCamera();
    this.hands = this.buildHands();
    this.cues = [...project.subtitles].sort((a, b) => a.t0 - b.t0);
  }

  setTarget(target: RenderTarget): void {
    this.target = target;
    this.hands = this.buildHands();
  }

  /** Rebuild every element that uses `assetId` (after it was (re)loaded into the resources). */
  invalidateAsset(assetId: string): void {
    for (const el of this.project.elements) {
      const uses = el.image?.assetId === assetId || el.text?.fontAssetId === assetId || el.enter.handId === assetId;
      if (uses) this.runtimes.delete(el.id);
    }
    this.setProject(this.project);
  }

  /** Element size in board units at scale 1 (null until its resources are loaded). */
  elementSize(id: string): { w: number; h: number } | null {
    const rt = this.runtimes.get(id);
    return rt ? { w: rt.w * rt.unit, h: rt.h * rt.unit } : null;
  }

  /** Axis-aligned board box of an element at its resting place. */
  boardBounds(id: string): Rect | null {
    const rt = this.runtimes.get(id);
    if (!rt) return null;
    const t = rt.el.transform;
    const s = t.scale * rt.unit;
    const E = chain(translate(t.x, t.y), rotate(t.rotation), scale(s * (t.flipX ? -1 : 1), s * (t.flipY ? -1 : 1)), translate(-rt.w / 2, -rt.h / 2));
    const pts = [apply(E, 0, 0), apply(E, rt.w, 0), apply(E, rt.w, rt.h), apply(E, 0, rt.h)];
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
  }

  get currentProject(): Project {
    return this.project;
  }

  get timesById(): ReadonlyMap<string, ElementTimes> {
    return this.times;
  }

  /** Camera at t (auto-camera and manual keys resolved). */
  cameraAt(t: number): CameraState {
    return this.cam(t);
  }

  /** Auto keys (as generated) and the keyframes in effect. */
  get cameraKeys(): { auto: CameraKey[]; resolved: CameraKey[] } {
    return { auto: this.autoKeys, resolved: this.keys };
  }

  private updateCamera(): void {
    const p = this.project;
    if (p.camera.auto) {
      const bounds = new Map<string, Rect>();
      for (const el of p.elements) {
        const b = this.boardBounds(el.id);
        if (b) bounds.set(el.id, b);
      }
      this.autoKeys = autoCameraKeys(p, this.times, bounds);
    } else this.autoKeys = [];
    this.keys = resolveKeys(p, this.autoKeys);
    this.cam = makeCamera(p, this.keys);
  }

  // ------------------------------------------------------------------------------- frame

  /** Draw the frame at t. `camera` overrides the project camera (editor board view). */
  renderFrame(t: number, camera?: CameraState): void {
    const { ctx, width, height } = this.target;
    const project = this.project;
    const M = cameraMatrix(project, camera ?? this.cam(t), width, height);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    const bg = project.board.background;
    drawBackground(ctx as Ctx2D, bg, width, height, bg.kind === "image" && bg.assetId ? this.resources.backgrounds.get(bg.assetId) : undefined);

    // content being wiped or erased off the board goes through its own layer first
    const clearing = this.order.filter((el) => {
      const tm = this.times.get(el.id)!;
      return !el.hidden && tm.clearType && tm.clearType !== "cut" && t >= tm.clear0 && t < tm.cleared && t >= tm.enter0;
    });
    if (clearing.length) {
      const L = this.ensureClearLayer(width, height);
      L.ctx.setTransform(1, 0, 0, 1, 0, 0);
      L.ctx.clearRect(0, 0, width, height);
      L.ctx.imageSmoothingEnabled = true;
      L.ctx.imageSmoothingQuality = "high";
      for (const el of clearing) this.drawOne(L.ctx, el, t, M);
      const tm = this.times.get(clearing[0].id)!;
      this.applyClearMask(L.ctx, tm, t, width, height);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(L.canvas, 0, 0);
    }
    const skip = new Set(clearing.map((e) => e.id));
    for (const el of this.order) {
      if (el.hidden || skip.has(el.id)) continue;
      const tm = this.times.get(el.id)!;
      if (t < tm.enter0 || t >= tm.end) continue;
      this.drawOne(ctx as Ctx2D, el, t, M);
    }
    ctx.globalAlpha = 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    const hand = this.handAt(t, M);
    if (hand) hand.sprite.draw(ctx as Ctx2D, hand.x, hand.y, width / 1920, hand.lifted);

    if (project.captions.enabled) {
      const text = this.captionAt(t);
      const font = this.captionFont();
      if (text && font) drawCaption(ctx as Ctx2D, font, text, project.captions, width, height);
    }
  }

  private drawOne(ctx: Ctx2D, el: Element, t: number, M: Mat): void {
    const rt = this.runtimes.get(el.id);
    if (!rt) return;
    const tm = this.times.get(el.id)!;
    const ph = phaseAt(el, tm, t);
    const alpha = elementAlpha(rt, ph);
    if (alpha <= 0) return;
    const E = mul(M, elementMatrix(this.project, rt, tm, ph, this.cam));
    ctx.setTransform(E[0], E[1], E[2], E[3], E[4], E[5]);
    ctx.globalAlpha = alpha;
    drawElement(ctx, rt, ph);
    ctx.globalAlpha = 1;
  }

  private ensureClearLayer(w: number, h: number) {
    if (!this.clearLayer || this.clearLayer.canvas.width !== w || this.clearLayer.canvas.height !== h) this.clearLayer = makeCanvas(w, h);
    return this.clearLayer;
  }

  private applyClearMask(ctx: Ctx2D, tm: ElementTimes, t: number, W: number, H: number): void {
    const dur = tm.cleared - tm.clear0;
    const u = dur > 0 ? clamp((t - tm.clear0) / dur, 0, 1) : 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = "destination-out";
    if (tm.clearType === "wipe") {
      const e = ease("easeInOut", u);
      ctx.fillStyle = "#000";
      if (tm.clearDir === "right") ctx.fillRect(0, 0, Math.ceil(e * W), H);
      else if (tm.clearDir === "left") ctx.fillRect(Math.floor((1 - e) * W), 0, W, H);
      else if (tm.clearDir === "down") ctx.fillRect(0, 0, W, Math.ceil(e * H));
      else ctx.fillRect(0, Math.floor((1 - e) * H), W, H);
    } else if (tm.clearType === "erase") {
      const er = this.eraseFor(tm.clear0, dur, W, H);
      er.mask.update(er.pen.at(t - tm.clear0).revealed);
      ctx.drawImage(er.mask.canvas, 0, 0);
    }
    ctx.restore();
  }

  /** The scrubbing path for an erase transition, in output pixels (cached per size). */
  private eraseFor(t0: number, dur: number, W: number, H: number) {
    const key = `${t0}|${dur}|${W}x${H}`;
    let e = this.erasers.get(key);
    if (!e) {
      const r = H / 9;
      const set = buildStrokeSet([zigzagStroke(0, 0, W, H, r)]);
      e = { pen: new PenTimeline(set, { duration: Math.max(0.05, dur), unitScale: this.project.output.width / W }), mask: new MaskRevealer(W, H, set) };
      if (this.erasers.size > 16) this.erasers.clear();
      this.erasers.set(key, e);
    }
    return e;
  }

  /** Caption text at t: the timed cue if the project has subtitles, else the scene's caption. */
  captionAt(t: number): string | null {
    const cues = this.cues;
    if (cues.length) {
      let lo = 0;
      let hi = cues.length - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (cues[mid].t0 <= t) lo = mid;
        else hi = mid - 1;
      }
      const c = cues[lo];
      return c.t0 <= t && t < c.t1 ? c.text : null;
    }
    const scenes = sortedScenes(this.project);
    return scenes[sceneIndexAt(scenes, t)]?.caption ?? null;
  }

  private captionFont() {
    const c = this.project.captions;
    const id = c.fontAssetId ?? this.project.assets.find((a) => a.kind === "font")?.id;
    return id ? this.resources.fonts.get(id)?.font : undefined;
  }

  /** Hand position (output pixels) at time t, or null when the hand is off the board. */
  handPosition(t: number): { x: number; y: number; lifted: number } | null {
    const { width, height } = this.target;
    const M = cameraMatrix(this.project, this.cam(t), width, height);
    const h = this.handAt(t, M);
    return h ? { x: h.x, y: h.y, lifted: h.lifted } : null;
  }

  // ------------------------------------------------------------------------------- hand

  private handFor(el: Element): HandSprite | null {
    const id = el.enter.handId ?? this.project.defaults.handId;
    if (id === "none") return null;
    if (id === "marker") return MARKER;
    const h = this.resources.hands.get(id);
    if (!h) return MARKER;
    const asset = this.project.assets.find((a) => a.id === id);
    return imageHand(h.img, h.anchor.x, h.anchor.y, asset?.scale ?? 1);
  }

  private buildHands(): HandInterval[] {
    const out: HandInterval[] = [];
    for (const el of this.project.elements) {
      if (el.hidden) continue;
      const tm = this.times.get(el.id)!;
      const sprite = this.handFor(el);
      if (sprite && hasPen(el) && tm.enter1 > tm.enter0) {
        out.push({ t0: tm.enter0, t1: Math.min(tm.enter1, tm.end), sprite, point: (t, M) => this.penPoint(el, t, M) });
      }
      if (el.exit.mode === "erase" && Number.isFinite(tm.exit0) && tm.exit0 < tm.end) {
        out.push({ t0: tm.exit0, t1: tm.end, sprite: ERASER, point: (t, M) => this.eraserPoint(el, t, M) });
      }
    }
    // erase transitions sweep the whole frame
    const scenes = sortedScenes(this.project);
    scenes.forEach((s, i) => {
      if (i === 0 || s.startMode !== "clear" || s.transition.type !== "erase" || s.transition.duration <= 0) return;
      const t0 = s.start;
      const dur = s.transition.duration;
      out.push({
        t0,
        t1: t0 + dur,
        sprite: ERASER,
        point: (t) => {
          const { width: W, height: H } = this.target;
          const p = this.eraseFor(t0, dur, W, H).pen.at(clamp(t, t0, t0 + dur - 1e-6) - t0);
          return { x: p.x, y: p.y, down: true, smooth: 0.01 };
        },
      });
    });
    return out.sort((a, b) => a.t0 - b.t0);
  }

  /** Pen position of an element in output pixels (t clamped into its entrance). */
  private penPoint(el: Element, t: number, M: Mat): HandPoint | null {
    const rt = this.runtimes.get(el.id);
    const tm = this.times.get(el.id);
    if (!rt || !tm) return null;
    const tc = Math.min(Math.max(t, tm.enter0), tm.enter1 - 1e-6);
    const ph = phaseAt(el, tm, tc);
    const B = elementMatrix(this.project, rt, tm, ph, this.cam);
    if (el.enter.mode === "moveIn") {
      const [x, y] = apply(M, ...apply(B, rt.w / 2, rt.h / 2));
      return { x, y, down: true, smooth: HAND.drawSmooth };
    }
    const tip = penTip(rt, ph, tc);
    if (!tip) return null;
    const [x, y] = apply(M, ...apply(B, tip.x, tip.y));
    const smooth = tip.phase === "second" ? HAND.fillSmooth : tip.down ? HAND.drawSmooth : HAND.liftSmooth;
    return { x, y, down: tip.down, smooth };
  }

  private eraserPoint(el: Element, t: number, M: Mat): HandPoint | null {
    const rt = this.runtimes.get(el.id);
    const tm = this.times.get(el.id);
    if (!rt || !tm) return null;
    const tc = Math.min(Math.max(t, tm.exit0), tm.end - 1e-6);
    const ph = phaseAt(el, tm, tc);
    const tip = eraserTip(rt, ph);
    if (!tip) return null;
    const B = elementMatrix(this.project, rt, tm, ph, this.cam);
    const [x, y] = apply(M, ...apply(B, tip.x, tip.y));
    return { x, y, down: true, smooth: 0.01 };
  }

  private smoothed(iv: HandInterval, t: number, M: Mat) {
    const p = iv.point(t, M);
    if (!p) return null;
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (let k = -3; k <= 3; k++) {
      const q = k === 0 ? p : iv.point(t + (k * p.smooth) / 3, M);
      if (!q) continue;
      sx += q.x;
      sy += q.y;
      n++;
    }
    return { x: sx / n, y: sy / n, lifted: p.down ? 0 : 1 };
  }

  private handAt(t: number, M: Mat): { sprite: HandSprite; x: number; y: number; lifted: number } | null {
    const ivs = this.hands;
    if (ivs.length === 0) return null;
    const { width, height } = this.target;
    const off = { x: width * 0.92, y: height * 1.45 }; // below the bottom-right corner

    // working now? the most recently started job owns the hand
    let active: HandInterval | null = null;
    for (const iv of ivs) if (iv.t0 <= t && t < iv.t1) active = iv;
    if (active) {
      const s = this.smoothed(active, t, M);
      if (s) return { sprite: active.sprite, ...s };
    }
    let prev: HandInterval | null = null;
    let next: HandInterval | null = null;
    for (const iv of ivs) {
      if (iv.t1 <= t && (!prev || iv.t1 > prev.t1)) prev = iv;
      if (iv.t0 > t && (!next || iv.t0 < next.t0)) next = iv;
    }
    if (prev && next && next.t0 - prev.t1 <= HAND.bridgeGap && prev.sprite === next.sprite) {
      const a = prev.point(prev.t1, M);
      const b = next.point(next.t0, M);
      if (a && b) {
        const u = smoothstep(clamp((t - prev.t1) / (next.t0 - prev.t1), 0, 1));
        return { sprite: next.sprite, x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u, lifted: 1 };
      }
    }
    if (prev && t < prev.t1 + HAND.exit) {
      const a = prev.point(prev.t1, M);
      if (a) {
        const u = ease("easeIn", (t - prev.t1) / HAND.exit);
        return { sprite: prev.sprite, x: a.x + (off.x - a.x) * u, y: a.y + (off.y - a.y) * u, lifted: 1 };
      }
    }
    // entering: never before t = 0, so the first frame doesn't catch the hand mid-flight
    const enter = next ? Math.min(HAND.enter, Math.max(next.t0, 0.12)) : HAND.enter;
    if (next && t > next.t0 - enter) {
      const b = next.point(next.t0, M);
      if (b) {
        const u = ease("easeOut", 1 - (next.t0 - t) / enter);
        return { sprite: next.sprite, x: off.x + (b.x - off.x) * u, y: off.y + (b.y - off.y) * u, lifted: 1 };
      }
    }
    return null;
  }
}

function hasPen(el: Element): boolean {
  if (el.enter.mode === "moveIn") return true;
  return el.enter.mode === "draw" || el.enter.mode === "outline";
}
